import { Button, Checkbox, FormControlLabel, TextField } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { lookupClientRequest } from '../../api/documentApi'
import { Client } from '../../redux/reducers/clients'
import { tokens } from '../../styles/tokens'

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  field: {
    flexGrow: 1,
    minWidth: '180px',
  },
  hint: {
    margin: 0,
    fontSize: '0.75rem',
    color: tokens.color.neutral[500],
  },
  duplicate: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: 0,
    fontSize: '0.8rem',
    color: tokens.color.warning[700],
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    borderTop: `1px solid ${tokens.color.neutral[200]}`,
    paddingTop: theme.spacing(1),
  },
}))

// Volitelne akce po zalozeni klienta — posilaji se az kdyz generovani projde.
export interface NewClientOptions {
  sendCredentials: boolean
  sendWelcomeEmail: boolean
}

interface Props {
  value: Partial<Client>
  onChange: (client: Partial<Client>) => void
  onUseExisting: (client: Client) => void
  options: NewClientOptions
  onOptionsChange: (options: NewClientOptions) => void
}

// Minimalni formular noveho klienta pro generovani dokumentu.
// Povinne jen prijmeni + RC/ICO — BE klienta zalozi jako "nedokonceny" (incomplete).
export const NewClientForm: React.FC<Props> = ({ value, onChange, onUseExisting, options, onOptionsChange }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [duplicates, setDuplicates] = useState<Client[]>([])
  const hasEmail = !!(value.email || '').trim()

  const setField = (name: keyof Client) => (event: React.ChangeEvent<{ value: string }>) => {
    onChange({ ...value, [name]: event.target.value })
  }

  const handleLookup = async () => {
    const personalNumber = (value.personalNumber || '').replace(/[\s/]/g, '')
    const companyId = (value.companyId || '').trim()
    if (!personalNumber && !companyId) {
      setDuplicates([])
      return
    }
    try {
      const response = await lookupClientRequest(personalNumber, companyId)
      setDuplicates(response.data || [])
    } catch (error) {
      console.error(error)
    }
  }

  const duplicateName = (client: Client) =>
    client.company ? client.companyName : [client.firstName, client.surname].filter(Boolean).join(' ')

  return (
    <div className={classes.form}>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!value.company}
            onChange={event => onChange({ ...value, company: event.target.checked })}
          />
        }
        label="Právnická osoba"
      />

      <div className={classes.row}>
        {value.company && (
          <>
            <TextField
              variant="standard"
              className={classes.field}
              label="Název společnosti"
              value={value.companyName || ''}
              onChange={setField('companyName')}
            />
            <TextField
              variant="standard"
              className={classes.field}
              label="IČO *"
              value={value.companyId || ''}
              onChange={setField('companyId')}
              onBlur={handleLookup}
            />
          </>
        )}
        <TextField
          variant="standard"
          className={classes.field}
          label={t('FIRST_NAME')}
          value={value.firstName || ''}
          onChange={setField('firstName')}
        />
        <TextField
          variant="standard"
          className={classes.field}
          label={t('SURNAME') + ' *'}
          value={value.surname || ''}
          onChange={setField('surname')}
        />
        {!value.company && (
          <TextField
            variant="standard"
            className={classes.field}
            label={t('PERSONAL_NUMBER') + ' *'}
            placeholder="RRMMDD/XXXX"
            value={value.personalNumber || ''}
            onChange={setField('personalNumber')}
            onBlur={handleLookup}
          />
        )}
      </div>
      <div className={classes.row}>
        <TextField
          variant="standard"
          className={classes.field}
          label="E-mail"
          type="email"
          value={value.email || ''}
          onChange={setField('email')}
        />
        <TextField
          variant="standard"
          className={classes.field}
          label="Telefon"
          value={value.telephoneNumber || ''}
          onChange={setField('telephoneNumber')}
        />
      </div>

      {duplicates.map(duplicate => (
        <p key={duplicate.clientId} className={classes.duplicate}>
          {t('CLIENT_ALREADY_EXISTS')}: {duplicateName(duplicate)}
          <Button size="small" variant="outlined" onClick={() => onUseExisting(duplicate)}>
            {t('USE_EXISTING_CLIENT')}
          </Button>
        </p>
      ))}

      <div className={classes.options}>
        <FormControlLabel
          control={
            <Checkbox
              checked={options.sendCredentials && hasEmail}
              disabled={!hasEmail}
              onChange={event => onOptionsChange({ ...options, sendCredentials: event.target.checked })}
            />
          }
          label={t('SEND_CREDENTIALS')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={options.sendWelcomeEmail && hasEmail}
              disabled={!hasEmail}
              onChange={event => onOptionsChange({ ...options, sendWelcomeEmail: event.target.checked })}
            />
          }
          label={t('SEND_WELCOME_EMAIL')}
        />
        {!hasEmail && <p className={classes.hint}>{t('CLIENT_EMAIL_REQUIRED_FOR_MAILS')}</p>}
      </div>

      <p className={classes.hint}>{t('INCOMPLETE_CLIENT_HINT')}</p>
    </div>
  )
}
